import { Application, Assets, Graphics, Sprite, AnimatedSprite, Container, ParticleContainer, Particle, Texture, Point, Text, TextStyle } from 'pixi.js';
import bgImagePath from 'url:./assets/bg.png'; 
import planetImagePath1 from 'url:./assets/planet02.png'; 
import planetImagePath2 from 'url:./assets/planet02_a.png'; 
import planetImagePath3 from 'url:./assets/planet02_b.png'; 

import holeImagePath1 from 'url:./assets/blackHole.png'; 
import holeImagePath2 from 'url:./assets/blackHole2.png'; 

import explodeImagePath1 from 'url:./assets/explode.png'; 
import explodeImagePath2 from 'url:./assets/explode2.png'; 

import gameoverImagePath from 'url:./assets/gameover.png'; 
import discordImagePath from 'url:./assets/discord.png';

import { DiscordSDK } from '@discord/embedded-app-sdk';
import { initAnalytics, identifyUser, track, platform } from './analytics.js';

initAnalytics();

// Global variable to keep the user ID accessible in the PixelJS rendering loops
let globalDiscordUserId = null;
let discordSdk = null;

async function authenticateDiscord() {
    // Only initialize SDK if playing inside a Discord Activity (checks for frame_id)
    const frameId = new URLSearchParams(window.location.search).get('frame_id');
    if (!frameId) {
        console.log("Not running in a Discord Activity. Skipping Discord SDK initialization.");
        return;
    }

    try {
        discordSdk = new DiscordSDK(process.env.NEXT_PUBLIC_DISCORD_CLIENT_ID || process.env.DISCORD_CLIENT_ID);
        await discordSdk.ready();
        
        // Authorize with Discord Client
        const { code } = await discordSdk.commands.authorize({
            client_id: process.env.NEXT_PUBLIC_DISCORD_CLIENT_ID || process.env.DISCORD_CLIENT_ID,
            response_type: "code",
            state: "",
            prompt: "none",
            scope: [
                "identify",
                "guilds",
                "applications.commands"
            ],
        });

        // Trade code for token via our Netlify Proxy Function
        const response = await fetch('/.netlify/functions/discord-token', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ code }),
        });

        if (!response.ok) {
            throw new Error(`Failed to exchange token. Status: ${response.status}`);
        }

        const { access_token } = await response.json();

        // Authenticate the SDK
        const authInfo = await discordSdk.commands.authenticate({
            access_token,
        });

        if (authInfo == null) {
            throw new Error("Authenticate command failed");
        }

        // Cache the Discord user ID locally
        globalDiscordUserId = authInfo.user.id;
        console.log("Discord authentication successful. User ID:", globalDiscordUserId);

        const guildId = discordSdk.guildId ?? "dm";
        identifyUser(globalDiscordUserId, { username: authInfo.user.username, guild_id: guildId });
        
    } catch (err) {
        console.error("Discord SDK authentication failed:", err);
    }
}

async function createApplication() {
    // Create a new PixiJS Application
    const app = new Application();

    const targetWidth = 720;
    const targetHeight = 1280;
    const aspectRatio = targetWidth / targetHeight;

    const width = 180;
    const height = 400;
    
    const scale = Math.min(width / targetWidth, height / targetHeight);

    // Initialize the application with settings
    await app.init({
        width: targetWidth * scale,
        height: targetHeight * scale,
        backgroundColor: 0x000000, // Black background
        antialias: true,           // Enable anti-aliasing
        resolution: 2, // Match device pixel ratio
    });

    // Start Discord Authentication as early as possible without blocking standard load
    authenticateDiscord();

    track("player_session", { game: "gravulous", platform });
    track("game_start", { game: "gravulous" });

    // Append the canvas to the document body
    document.body.appendChild(app.canvas);

    const uiLayer = new Container();
    const spriteLayer = new Container();

    app.stage.addChild(spriteLayer);
    app.stage.addChild(uiLayer);



    // Create a loading bar
    const loadingBar = new Graphics();
    loadingBar.fill(0xffffff);
    loadingBar.rect(0, 0, 300, 20);
    loadingBar.fill();
    loadingBar.x = (app.screen.width - 300) / 2;
    loadingBar.y = app.screen.height / 2;
    loadingBar.scale.x = 0; // Start with zero width
    app.stage.addChild(loadingBar);

    // Create loading text
    const loadingText = new Text({text:'Loading... 0%', style:new TextStyle({
        fill: 0xffffff,
        fontSize: 24,
    })});
    loadingText.anchor.set(0.5);
    loadingText.x = app.screen.width / 2;
    loadingText.y = app.screen.height / 2 - 40;
    app.stage.addChild(loadingText);

    // Load assets with progress tracking
    const assets = [
        { alias: 'bg', src: bgImagePath },
        { alias: 'planet1', src: planetImagePath1 },
        { alias: 'planet2', src: planetImagePath2 },
        { alias: 'planet3', src: planetImagePath3 },
        { alias: 'hole1', src: holeImagePath1 },
        { alias: 'hole2', src: holeImagePath2 },
        { alias: 'explode1', src: explodeImagePath1 },
        { alias: 'explode2', src: explodeImagePath2 },
        { alias: 'gameover', src: gameoverImagePath },
        { alias: 'discord', src: discordImagePath },
    ];

    // Add all assets to the manager before loading them
    for (const asset of assets) {
        Assets.add(asset);
    }

    async function loadAssetsWithProgress() {
        let loadedCount = 0;
    
        for (const asset of assets) {
            // Load each asset individually by alias
            await Assets.load(asset.alias);
    
            // Increment the count of loaded assets
            loadedCount++;
    
            // Calculate and update progress
            const progress = (loadedCount / assets.length) * 100;
            loadingBar.scale.x = progress / 100; // Update loading bar scale
            loadingText.text = `Loading... ${Math.round(progress)}%`; // Update loading text
            console.log(loadingText.text);
        }
    
        // Remove loading bar and text when done
        app.stage.removeChild(loadingBar);
        app.stage.removeChild(loadingText);
    
        initializeGame();
    }

    loadAssetsWithProgress();


    let background = null;

    async function createBackground() {
        // Load the texture
        const texture = Assets.get('bg'); // Load the imported path
        background = new Sprite(texture);

        // Set the background size and position
        background.width = app.screen.width;
        background.height = app.screen.height;
        background.anchor.set(0); // Align to the top-left corner
        app.stage.addChildAt(background, 0); // Add to the bottom of the stage

    }
    
    async function initializeGame()  {
        createBackground().catch((err) => console.error('Error loading background:', err));

        // Load the planet texture
        const planetTexture =  Assets.get('planet1');
        const planetTexture2 =  Assets.get('planet2');
        const planetTexture3 =  Assets.get('planet3');

        const holeTexture1 =  Assets.get('hole1');
        const holeTexture2 =  Assets.get('hole2');

        const explodeTexture1 =  Assets.get('explode1');
        const explodeTexture2 =  Assets.get('explode2');

        const gameoverTexture =  Assets.get('gameover');
        const discordTexture = Assets.get('discord');

        let gravX = app.screen.width / 2;
        let gravY = app.screen.height - 40;

        const GRAV_RADIUS = 20; // Radius of the white circle
        const GRAV_SIZE = 60
        // Draw the central circle
        const circle = new Sprite(holeTexture1);
        circle.anchor.set(0.5);
        circle.scale.set(1); // Reset scale
        circle.raidus = GRAV_RADIUS;

        circle.width = GRAV_SIZE; // Diameter
        circle.height = GRAV_SIZE; // Diameter

        // Position the circle near the bottom center
        circle.x = gravX; // Center horizontally
        circle.y = gravY; // 20 pixels above the bottom (radius = 20)
        spriteLayer.addChild(circle);

        const circle2 = new Sprite(holeTexture2);
        circle2.anchor.set(0.5);
        circle2.scale.set(1); // Reset scale
        circle2.raidus = GRAV_RADIUS;

        circle2.width = GRAV_SIZE; // Diameter
        circle2.height = GRAV_SIZE; // Diameter

        // Position the circle near the bottom center
        circle2.x = gravX; // Center horizontally
        circle2.y = gravY; // 20 pixels above the bottom (radius = 20)
        spriteLayer.addChild(circle2);

        console.log("screen width " + app.screen.width + " screen height " + app.screen.height + " scale: " + scale);
        

        // planet.fill(0x0000ff); // Blue color
        // planet.circle(0, 0, 10); // Radius of 10
        // planet.fill();
        // planet.x = gravX;
        // planet.y = gravY;
        // app.stage.addChild(planet);

        // Interaction setup
        // planet.interactive = true;
        // planet.buttonMode = true;

        let activePlanet;
        const restingPlanets = []; 

        // Physics variables
        let isDragging = false;
        let velocityX = 0;
        let velocityY = 0;
        const dragLine = new Graphics();

        // Add the drag line to the stage
        app.stage.addChild(dragLine);

        // Cap values
        const MAX_DISTANCE = 20; // Maximum drag distance
        const MAX_VELOCITY = 20;  // Maximum velocity
        const DAMPING = 0.95; 

        const SCALE_SPEED = 0.2; // Speed of scaling
        const GROWTH_SPEED = 10; // Speed of growth

        let createNewPlanet = false;
        let moving = false;
        let initialCollison = false;
        let score = 0; 
        let best = 0;
        // Text style for score
        const scoreStyle = new TextStyle({
            fontFamily: 'Arial',
            fontSize: 12,
            fill: 0xffffff,  // White color
            align: 'right',
        });
        

        // Create score text
        const scoreText = new Text({text:`Score: ${score}`, style: scoreStyle});
        scoreText.anchor.set(1, 1); // Align to bottom-right corner
        scoreText.x = app.screen.width - 10; // 10px padding from the right
        scoreText.y = app.screen.height - 60; // 10px padding from the bottom, moved up 50px

        uiLayer.addChild(scoreText);

        // Create score text
        const bestText = new Text({text:`Best: ${score}`, style: scoreStyle});
        bestText.anchor.set(0, 1); // Align to bottom-right corner
        bestText.x = 5; // 10px padding from the right
        bestText.y = app.screen.height - 60; // 10px padding from the bottom, moved up 50px
        uiLayer.addChild(bestText);

        const discordSprite = new Sprite(discordTexture);
        // Position below "Best" text score (which anchors at bottom-left: x=5, y=app.screen.height-10)
        discordSprite.x = 5;
        discordSprite.y = bestText.y + 5; // placing it just below the best score
        discordSprite.width = 15; // 50% smaller
        discordSprite.height = 15; // 50% smaller
        discordSprite.interactive = true;
        discordSprite.buttonMode = true; // Add buttonMode to fix clickability sometimes needed in Pixi
        discordSprite.cursor = 'pointer';
        
        const openDiscordLink = () => {
            if (discordSdk) {
                discordSdk.commands.openExternalLink({ url: 'https://discord.gg/28ndJ6MebN' });
            } else {
                window.open('https://discord.gg/28ndJ6MebN', '_blank');
            }
        };

        // Some environments require using 'pointerup' or 'tap' in addition to/instead of 'pointerdown'
        discordSprite.on('pointerup', openDiscordLink);
        discordSprite.on('touchend', openDiscordLink);
        uiLayer.addChild(discordSprite);

        const introStyle = new TextStyle({
            fontFamily: 'Arial',
            fontSize: 10,
            fill: 0xffffff,  // White color
            align: 'center',
        });
        let introText = new Text({text:`Click drag planet to slingshot\nit out of the Gravulous`, style: introStyle});
        introText.anchor.set(0.5, 0.5); // Align to bottom-right corner
        introText.x = app.screen.width/2; // 10px padding from the right
        introText.y = app.screen.height/2; // 10px padding from the bottom
        uiLayer.addChild(introText);

        
        const PLANET_BASE_RADIUS = 10; // Radius of the planets

        // Create a particle container with default options
        const container = new ParticleContainer({
            // this is the default, but we show it here for clarity
            dynamicProperties: {
                position: true,  // Allow dynamic position changes (default)
                scale: false,    // Static scale for extra performance
                rotation: false, // Static rotation
                color: false     // Static color
            }
        });

        // Add particles
        // const texture = Texture.from(planetImagePath);


        // for (let i = 0; i < 100; ++i) {
        //     let particle = new Particle({
        //         texture,
        //         x: Math.random() * 360,
        //         y: Math.random() * 640,
        //         scaleX: 0.15,
        //         scaleY: 0.15,
        //     });

        //     container.addParticle(particle);
        // }

        // Load textures for explosion animation
        const explodeTextures = [
            new Sprite(explodeTexture1),
            new Sprite(explodeTexture2)
        ];

        // Create an AnimatedSprite using the textures
        // const explosion = new AnimatedSprite(explodeTextures);

        // // // Set animation properties
        // explosion.animationSpeed = 0.1; // Speed of animation
        // explosion.loop = false; // Play animation only once
        // explosion.anchor.set(0.5); // Center the sprite

        // // Add container to the Pixi stage
        //app.stage.addChild(container);

        // Function to create a new planet
        function createPlanet() {
            // const planet = new Graphics();
            // planet.fill(0x0000ff); // Blue color
            // planet.circle(0, 0, PLANET_BASE_RADIUS); // Radius of 10
            // planet.fill();

            const planet = new Sprite(planetTexture);
            // Center the sprite for positioning
            planet.anchor.set(0.5);
            planet.scale.set(1); // Reset scale

            // Resize the planet sprite to match the desired size
            planet.width = PLANET_BASE_RADIUS * 2; // Diameter
            planet.height = PLANET_BASE_RADIUS * 2; // Diameter


            planet.x = gravX;
            planet.y = gravY;
            planet.baseRadius = PLANET_BASE_RADIUS; // Base radius before scaling
            
            planet.hits = 0;
            planet.getRadius = function () {
            // return this.baseRadius * this.scale.x;
            return this.width / 2;
            }; // Dynamically calculate scaled radius
            planet.interactive = true;
            planet.cursor = "pointer";

            spriteLayer.addChild(planet);

            // Add drag interaction to the active planet
            planet.on('pointerdown', () => {
                if (activePlanet !== planet) return; // Only allow dragging the active planet
                isDragging = true;
                if (introText) {
                    uiLayer.removeChild(introText);
                    introText = null;
                }
            });

            return planet;
        }

        // Create the first active planet
        activePlanet = createPlanet();


        const buttonStyle = new TextStyle({
            fontFamily: 'Arial',
            fontSize: 24,
            fill: 0x00FF00, // Green color
            align: 'center',
        });

        // Text style for "Game Over"
        const gameOverStyle = new TextStyle({
            fontFamily: 'Arial',
            fontSize: 36,
            fill: 0xff0000, // Red color
            align: 'center',
        });

        let gameOverText = null; // Reference to Game Over text
        let restartButton = null; // Reference to Restart button
        let gameOver = false; // Game over
        // Function to display "Game Over" text
        function displayGameOver() {
            if (gameOver) return; // Prevent multiple calls
            console.log("Game Over");
            velocityX = 0;
            velocityY = 0;

            gameOverText = new Sprite(gameoverTexture);
            gameOverText.anchor.set(0.5); // Center the text
            gameOverText.x = app.screen.width / 2;
            gameOverText.y = app.screen.height / 2-75;
            gameOverText.scale.x = 0.1;
            gameOverText.scale.y = 0.1;
            app.stage.addChild(gameOverText);

            restartButton = new Text({text:'Restart', style: buttonStyle});
            restartButton.anchor.set(0.5);
            restartButton.x = app.screen.width / 2;
            restartButton.y = app.screen.height / 2 + 40;
            restartButton.interactive = true;
            restartButton.buttonMode = true;

            // Restart game when the button is clicked
            restartButton.on('pointerdown', () => {
                track("game_restart", { game: "gravulous", score, guild_id: discordSdk?.guildId ?? "dm" });
                app.stage.removeChild(gameOverText);
                app.stage.removeChild(restartButton);
                resetGame();
            });

            app.stage.addChild(restartButton);

            gameOver = true; // Stop further actions

            app.ticker.add(function gameoverTicker() {
        
                gameOverText.scale.x += 0.02
                gameOverText.scale.y += 0.02
                // Check for collisions with canvas edges
                if (gameOverText.scale.x >= 0.75) {
                    app.ticker.remove(gameoverTicker); // Stop scaling
                    return;
                }
            });

        // Priority 1: From query param check for bot link sharing URLs 
        // Priority 2: From Discord Embedded App SDK (global state)
        // Priority 3: Fallback from previous manual URL params
        const finalUserId = getQueryParam('userId') || globalDiscordUserId || getQueryParam('user_id');
        
        track("game_complete", { game: "gravulous", score, guild_id: discordSdk?.guildId ?? "dm" });

        if (finalUserId) {
            console.log(`Sending score ${score} to Discord Webhook for user ${finalUserId}`);
            fetch('/.netlify/functions/score', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    userId: finalUserId,
                    gameId: 'gravulous',
                    score: score,
                }),
            })
            .then(async (response) => {
                if(!response.ok) {
                    const txt = await response.text();
                    console.error("Score Webhook Post Failed", response.status, txt);
                } else {
                    console.log("Score posted successfully wrapper");
                }
            })
            .catch((err) => {
                 console.error("Score Webhook Request Error", err);
            });
        } else {
             console.log("No valid userId found. Skipping score webhook post.");
        }
        
        const chat_id = getQueryParam('chat_id');
        const message_id = getQueryParam('message_id');
        const inline_message_id = getQueryParam('inline_message_id');
        
        // Original standard post_score proxy backward compatibility if needed:
        if(chat_id || message_id || inline_message_id) {
            fetch('/post_score', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    user_id: getQueryParam('user_id'),
                    chat_id,
                    message_id,
                    inline_message_id,
                    score: score,
                }),
            }).catch(e => console.error("Legacy /post_score failed", e));
        }
        }

        // Function to reset the game state
        function resetGame() {
            // Clear all resting planets
            for (const planet of restingPlanets) {
                spriteLayer.removeChild(planet);
            }
            restingPlanets.length = 0;

            // Reset variables
            velocityX = 0;
            velocityY = 0;
            isDragging = false;
            gameOver = false;
            best = score;
            score = 0;
            updateScore();
            if (activePlanet) {
                spriteLayer.removeChild(activePlanet);
            }

            moving = false;
            initialCollison = false;

            // Create a new active planet
            activePlanet = createPlanet();
        }

        // Drag end (shoot)
        const onPointerUp = () => {
        
            if (!isDragging) return;
            isDragging = false;

            const rect = app.canvas.getBoundingClientRect(); // Get canvas bounds
            const localX = event.clientX - rect.left;     // Pointer's X relative to canvas
            const localY = event.clientY - rect.top;     // Pointer's Y relative to canvas  
            let position = new Point(localX/2, localY/2);
            // Calculate drag vector
            const dx = gravX - position.x;
            const dy = gravY - position.y;
            let distance = Math.sqrt(dx * dx + dy * dy);

            // Cap the distance
            if (distance > MAX_DISTANCE) {
                distance = MAX_DISTANCE;
            }

            // Scale velocity based on distance
            const angle = Math.atan2(dy, dx);
            const speed = (distance / MAX_DISTANCE) * MAX_VELOCITY;
            velocityX = Math.cos(angle) * speed;
            velocityY = Math.sin(angle) * speed;

            // Clear the drag line
            dragLine.clear();

            initialCollison = true;
            moving = true;
        };

        // Update loop
        app.ticker.add(() => {

            circle.rotation += 0.005;
            circle2.rotation += 0.01;

            if (!isDragging && activePlanet) {
                // Update position
                activePlanet.x += velocityX;
                activePlanet.y += velocityY;

                if (moving)
                {
                    activePlanet.rotation += 0.005;
                }
                
                // Apply damping to slow down the planet
                velocityX *= DAMPING;
                velocityY *= DAMPING;

                // Check collision with the white circle
                if (moving) {
            
                    if (detectGravCollision(activePlanet)) {
                        if (!initialCollison){
                            displayGameOver();
                            return;
                        }
                    }
                    else {
                        initialCollison = false;
                    }

                }

                // Handle collisions with resting planets

                let removingPlanets = []; 
                for (const restingPlanet of restingPlanets) {
                    const dx = restingPlanet.x - activePlanet.x;
                    const dy = restingPlanet.y - activePlanet.y;
                    const distance = Math.sqrt(dx * dx + dy * dy);

                    if (distance < activePlanet.getRadius() + restingPlanet.getRadius()) {
                        handleCollision(activePlanet, restingPlanet);
                        restingPlanet.hits++;
                        if (restingPlanet.hits == 1) {
                            restingPlanet.texture = planetTexture2
                        }
                        else if (restingPlanet.hits == 2) {
                            restingPlanet.texture = planetTexture3
                        }

                        if (restingPlanet.hits >= 3) {
                            removingPlanets.push(restingPlanet);
                        }
                    }
                }

                if (removingPlanets.length > 0) {
                    for (const planet of removingPlanets) {
                        spriteLayer.removeChild(planet);
                        restingPlanets.splice(restingPlanets.indexOf(planet), 1);
                        score++;
                        updateScore();

                        // Add explosion sprite to the stage
                        // app.stage.addChild(explosion);
                        // explosion.position = planet.position;

                        // explosion.play();
                        // // Start the animation when needed
                        // explosion.onComplete = () => {
                        //     console.log('Explosion animation completed');
                        //     app.stage.removeChild(explosion); // Optional: Remove sprite after animation
                        //     explosion.stop();
                        //     explosion.currentFrame = 0;
                    // };

                    }
                }

                // Stop movement when velocity is very small
                if (moving && Math.abs(velocityX) < 0.01 && Math.abs(velocityY) < 0.01) {
                    velocityX = 0;
                    velocityY = 0;
                    moving = false;

                    // Add the current active planet to the resting planets array
                    restingPlanets.push(activePlanet);

                    // Scale up the resting planet
                    scalePlanet(activePlanet);
                    activePlanet = null;
                }

                if (activePlanet != null) {
                    // Handle wall collisions (bounce effect)
                    if (activePlanet.x <= 10 || activePlanet.x >= app.screen.width - 10) {
                        velocityX *= -1; // Reverse velocity
                        activePlanet.x = Math.max(10, Math.min(activePlanet.x, app.screen.width - 10));
                    }
                    if (activePlanet.y <= 10 || activePlanet.y >= app.screen.height - 10) {
                        velocityY *= -1; // Reverse velocity
                        activePlanet.y = Math.max(10, Math.min(activePlanet.y, app.screen.height - 10));
                    }
                }
            }

            // Create a new active planet
            if (createNewPlanet) {
                activePlanet = createPlanet();
                createNewPlanet = false;
            }
        });

        // Visual feedback for drag
        //app.stage.interactive = true;

        const onPointerMove = (event) => {
            if (isDragging) {
                
                const rect = app.canvas.getBoundingClientRect(); // Get canvas bounds
                const localX = event.clientX - rect.left;     // Pointer's X relative to canvas
                const localY = event.clientY - rect.top;     // Pointer's Y relative to canvas  
                let position = new Point(localX/2, localY/2);

                // Clear and redraw drag line
                dragLine.clear();
                dragLine.moveTo(gravX, gravY)
                    .lineTo(position.x, position.y)
                    .stroke({ color: 0xff0000 });
            
                // Position the active planet while dragging
                //activePlanet.x = position.x;
            // activePlanet.y = position.y;
            }
        };

        // Function to detect collision between two planets
        function detectCollision(planet1, planet2) {
            const dx = planet2.x - planet1.x;
            const dy = planet2.y - planet1.y;
            const distance = Math.sqrt(dx * dx + dy * dy);
            return distance < planet1.getRadius() + planet2.getRadius();
        }

        // Function to detect collision between two planets
        function detectGravCollision(planet) {
            const dx = planet.x - gravX;
            const dy = planet.y - gravY;
            const distance = Math.sqrt(dx * dx + dy * dy);
            return distance < planet.getRadius() + GRAV_RADIUS;
        }

        // Function to handle collision response and resolve overlap
        function handleCollision(movingPlanet, restingPlanet) {
            const dx = restingPlanet.x - movingPlanet.x;
            const dy = restingPlanet.y - movingPlanet.y;
            const distance = Math.sqrt(dx * dx + dy * dy);

            // Normalize the collision vector
            const nx = dx / distance;
            const ny = dy / distance;

            // Calculate overlap distance
            const overlap = movingPlanet.getRadius() + restingPlanet.getRadius() - distance;

            // Resolve overlap by pushing the moving planet away
            movingPlanet.x -= nx * overlap;
            movingPlanet.y -= ny * overlap;

            // Reflect the velocity of the moving planet
            const dot = velocityX * nx + velocityY * ny; // Dot product of velocity and normal
            velocityX -= 2 * dot * nx;
            velocityY -= 2 * dot * ny;
        }

        // Function to handle scaling of a stopped planet
        function scalePlanet(planet) {
            app.ticker.add(function scaleTicker() {
                //planet.scale.x += SCALE_SPEED;
                //planet.scale.y += SCALE_SPEED;

                planet.width = planet.width + GROWTH_SPEED; // Diameter
                planet.height = planet.height + GROWTH_SPEED; // Diameter

                // Check for collision with white circle
                
                if (detectGravCollision(planet)
                ) {
                    app.ticker.remove(scaleTicker);
                    displayGameOver();
                    return;
                }

                // Check for collisions with other planets
                for (const otherPlanet of restingPlanets) {
                    if (planet !== otherPlanet && detectCollision(planet, otherPlanet)) {
                        app.ticker.remove(scaleTicker); // Stop scaling
                        createNewPlanet = true;

                        return;
                    }
                }

                // Check for collisions with canvas edges
                if (
                    planet.x - planet.getRadius() <= 0 ||
                    planet.x + planet.getRadius() >= app.screen.width ||
                    planet.y - planet.getRadius() <= 0 ||
                    planet.y + planet.getRadius() >= app.screen.height
                ) {
                    app.ticker.remove(scaleTicker); // Stop scaling
                    createNewPlanet = true;
                    return;
                }
            });
        }

        function updateScore() {
            scoreText.text = `Score: ${score}`
            bestText.text = `Best: ${best}`
        }


        app.stage.on('pointermove', onPointerMove);
        // Attach global listeners
        window.addEventListener('pointermove', onPointerMove);
        window.addEventListener('pointerup', onPointerUp);

        function getQueryParam(param) {
            const urlParams = new URLSearchParams(window.location.search);
            return urlParams.get(param);
        }

    }
}


// Call the function to set up the application
createApplication().catch((err) => {
    console.error('Error initializing application:', err);
});
